import {
  type StudyStepSlug,
  type StudyStepStatus,
  type StudyStepView,
  studyStepNumber,
} from '@sigora/domain';

import { Modal } from '../ui/Modal.tsx';

/**
 * The one word each step's standing is reported as. The step on screen says "You are here"
 * whatever its standing, so `current` — where the study's work stands — is said of a step the
 * reader is not on.
 */
const STATUS_WORDS: Record<StudyStepStatus, string> = {
  complete: 'Done',
  current: 'In progress',
  available: 'Open',
  locked: 'Not yet',
  unimplemented: 'Not built yet',
};

export interface StudySoFarProps {
  steps: readonly StudyStepView[];
  /** Which step the sheet was opened from, so it can say where the reader is. */
  stepSlug: StudyStepSlug;
  onOpenStep: (slug: StudyStepSlug) => void;
  onClose: () => void;
}

/**
 * Every step, one line each: the only overview in the product.
 *
 * A sheet rather than a permanent column, because the design is one task on screen — an overview
 * always visible is a second task, and the guided flow is what it exists to avoid.
 *
 * Every step the study has reached opens from its name, done or not and whether or not the study
 * can still change: a step of a paid or launched study opens read-only and says so. A step the
 * study has not reached is named and nothing more, since there is nothing on it yet to show.
 */
export const StudySoFar = ({ steps, stepSlug, onOpenStep, onClose }: StudySoFarProps) => (
  <Modal
    className="study-so-far"
    closeTestId="study-so-far-close"
    onClose={onClose}
    open
    testId="study-so-far"
    title="Study so far"
  >
    <ol className="study-so-far-steps">
      {steps.map((step) => {
        const here = step.slug === stepSlug;
        const name = `${String(studyStepNumber(step.slug))}. ${step.name}`;

        return (
          <li
            aria-current={here ? 'step' : undefined}
            className={`study-so-far-step study-so-far-step-${step.status}`}
            data-step-status={step.status}
            data-test-id={`step-${step.slug}`}
            key={step.slug}
          >
            {here || step.status === 'locked' ? (
              <span className="study-so-far-name">{name}</span>
            ) : (
              <button
                type="button"
                className="study-so-far-name study-so-far-open"
                data-test-id={`open-step-${step.slug}`}
                onClick={() => onOpenStep(step.slug)}
              >
                {name}
              </button>
            )}
            <span className="study-so-far-status">
              {here ? 'You are here' : STATUS_WORDS[step.status]}
            </span>
            {step.summary !== null && <span className="study-so-far-summary">{step.summary}</span>}
          </li>
        );
      })}
    </ol>
  </Modal>
);
